import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Target, Plus, ExternalLink } from "lucide-react";
import { useGrowthOnePagerEditor } from "@/contexts/GrowthOnePagerEditorContext";
import GrowthPillarCard from "./GrowthPillarCard";
import { Link } from "react-router-dom";

interface SalesChannelCardProps {
  channel: {
    id: string;
    name: string;
    description: string;
    created_at: string;
  };
}

const SalesChannelCard = ({ channel }: SalesChannelCardProps) => {
  const { getPillarsForChannel, isLoading } = useGrowthOnePagerEditor();
  const growthPillars = getPillarsForChannel(channel.id);

  // Sort pillars by name
  const sortedPillars = [...growthPillars].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Card className="shadow-lg border-0 bg-gradient-to-r from-slate-50 to-slate-100">
      <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-t-lg pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <ShoppingCart className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{channel.name}</CardTitle>
              <p className="text-slate-200 text-sm mt-1">{channel.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="secondary" className="bg-white/20 text-white text-xs">
              {growthPillars.length} {growthPillars.length === 1 ? 'Pillar' : 'Pillars'}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-7 text-xs"
            >
              <Link to={`/growth-pillars/${channel.id}`}>
                <ExternalLink className="h-3 w-3 mr-1" />
                Manage
              </Link>
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        {sortedPillars.length === 0 ? (
          <div className="text-center py-6">
            <Target className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 mb-3 text-sm">No growth pillars defined yet</p>
            <Button asChild variant="outline" size="sm">
              <Link to={`/growth-pillars/${channel.id}`}>
                <Plus className="h-3 w-3 mr-2" />
                Create First Pillar
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                {sortedPillars.map((pillar, index) => (
                  <GrowthPillarCard
                    key={pillar.id}
                    pillar={pillar}
                    salesChannelId={channel.id}
                    order={index + 1}
                  />
                ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesChannelCard;
